import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, BookmarkCheck, BookmarkPlus, ChevronDown, ChevronUp, Lock, Rocket, X } from 'lucide-react';
import { PermissionGate } from '../components/PermissionGate';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { usePermissionContext } from '../contexts/PermissionContext';
import { ipoService } from '../services/ipoService';
import type { BankerEvaluation, IPOEntry, WatchQueueItem } from '../types/ipo';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function formatNumber(n: number | null): string {
  if (n === null) return '—';
  return n.toLocaleString();
}

function formatPrice(low: number | null, high: number | null): string {
  if (low === null) return '—';
  if (high !== null && high !== low) return `$${low} – $${high}`;
  return `$${low}`;
}

// ─── Watch Queue Status Badge ─────────────────────────────────────────────────

function WatchStatusBadge({ status }: { status: WatchQueueItem['status'] }) {
  if (status === 'awaiting') {
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">
        Awaiting
      </span>
    );
  }
  if (status === 'listed') {
    return (
      <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">
        Listed
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
      Stale
    </span>
  );
}

// ─── Banker Evaluation Badge ──────────────────────────────────────────────────

function BankerBadge({ evaluation, isPremium }: { evaluation: BankerEvaluation | null; isPremium: boolean }) {
  const [expanded, setExpanded] = useState(false);

  if (!isPremium) {
    return (
      <div className="relative group inline-flex">
        <Lock size={14} className="text-slate-500 cursor-help" />
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-slate-900 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
          Upgrade to Premium for AI evaluation
        </span>
      </div>
    );
  }

  if (!evaluation) {
    return <span className="text-xs text-slate-600">—</span>;
  }

  const score = evaluation.gate_score;
  const badgeColor = score >= 80
    ? 'bg-green-600 text-green-100'
    : score >= 60
    ? 'bg-amber-600 text-amber-100'
    : 'bg-red-700 text-red-100';

  return (
    <div className="inline-block">
      <button
        onClick={() => setExpanded(e => !e)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badgeColor} transition-opacity hover:opacity-90`}
      >
        {score.toFixed(0)}%
        {evaluation.strategy_matched && <span className="opacity-80 text-xs">· {evaluation.strategy_matched}</span>}
        {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>

      {expanded && (
        <div className="mt-2 p-3 bg-slate-900 border border-slate-700 rounded text-xs text-slate-300 max-w-md space-y-2 transition-all duration-200">
          {evaluation.technical_rationale && (
            <div>
              <p className="text-slate-400 font-medium mb-0.5">Analyst Rationale</p>
              <p className="text-slate-300 leading-relaxed">{evaluation.technical_rationale}</p>
            </div>
          )}
          {evaluation.thesis && (
            <div>
              <p className="text-slate-400 font-medium mb-0.5">Investment Thesis</p>
              <p className="text-slate-300 leading-relaxed">{evaluation.thesis}</p>
            </div>
          )}
          {evaluation.contra_summary && (
            <div>
              <p className="text-slate-400 font-medium mb-0.5">Contra / Risk</p>
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{evaluation.contra_summary}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Watch Queue Button ────────────────────────────────────────────────────────

function WatchQueueButton({
  entry,
  watchQueue,
  onAdd,
  onRemove,
  isAddPending,
  isRemovePending,
}: {
  entry: IPOEntry;
  watchQueue: WatchQueueItem[];
  onAdd: (ipoCalendarId: number) => void;
  onRemove: (itemId: number) => void;
  isAddPending: boolean;
  isRemovePending: boolean;
}) {
  const watchItem = watchQueue.find(w => w.ipo_calendar_id === entry.id);

  if (watchItem) {
    return (
      <div className="flex items-center gap-1.5">
        <WatchStatusBadge status={watchItem.status} />
        <button
          onClick={() => onRemove(watchItem.id)}
          disabled={isRemovePending}
          title="Remove from watch queue"
          className="text-slate-500 hover:text-red-400 transition-colors disabled:opacity-50"
        >
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => onAdd(entry.id)}
      disabled={isAddPending}
      title="Add to watch queue"
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-slate-600 text-slate-400 hover:text-white hover:border-blue-500 transition-colors disabled:opacity-50"
    >
      <BookmarkPlus size={12} />
      Watch
    </button>
  );
}

// ─── Stock IPOs Tab ────────────────────────────────────────────────────────────

interface StockFilters {
  exchange: string;
  sector: string;
  dateFrom: string;
  dateTo: string;
  watchOnly: boolean;
}

function StockIPOTab({
  entries,
  isPremium,
  watchQueue,
  onAddToWatch,
  onRemoveFromWatch,
  isAddPending,
  isRemovePending,
}: {
  entries: IPOEntry[];
  isPremium: boolean;
  watchQueue: WatchQueueItem[];
  onAddToWatch: (ipoCalendarId: number) => void;
  onRemoveFromWatch: (itemId: number) => void;
  isAddPending: boolean;
  isRemovePending: boolean;
}) {
  const stockEntries = entries.filter(e => e.asset_type === 'stock');

  const [filters, setFilters] = useState<StockFilters>({
    exchange: '', sector: '', dateFrom: '', dateTo: '', watchOnly: false,
  });

  const exchanges = useMemo(() => {
    const s = new Set(stockEntries.map(e => e.exchange).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [stockEntries]);

  const sectors = useMemo(() => {
    const s = new Set(stockEntries.map(e => e.sector).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [stockEntries]);

  const watchedIds = useMemo(() => new Set(watchQueue.map(w => w.ipo_calendar_id)), [watchQueue]);

  const filtered = useMemo(() => {
    return stockEntries.filter(e => {
      if (filters.exchange && e.exchange !== filters.exchange) return false;
      if (filters.sector && e.sector !== filters.sector) return false;
      if (filters.dateFrom && e.expected_date && e.expected_date < filters.dateFrom) return false;
      if (filters.dateTo && e.expected_date && e.expected_date > filters.dateTo) return false;
      if (filters.watchOnly && !watchedIds.has(e.id)) return false;
      return true;
    });
  }, [stockEntries, filters, watchedIds]);

  const clearFilters = () =>
    setFilters({ exchange: '', sector: '', dateFrom: '', dateTo: '', watchOnly: false });

  const hasFilters = filters.exchange !== '' || filters.sector !== '' || filters.dateFrom !== '' || filters.dateTo !== '' || filters.watchOnly;

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={filters.exchange}
          onChange={e => setFilters(f => ({ ...f, exchange: e.target.value }))}
          className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
        >
          <option value="">All Exchanges</option>
          {exchanges.map(ex => <option key={ex} value={ex}>{ex}</option>)}
        </select>

        <select
          value={filters.sector}
          onChange={e => setFilters(f => ({ ...f, sector: e.target.value }))}
          className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
        >
          <option value="">All Sectors</option>
          {sectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">From:</label>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">To:</label>
          <input
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))}
            className="bg-slate-700 border border-slate-600 rounded px-2 py-1.5 text-sm text-white"
          />
        </div>

        <button
          onClick={() => setFilters(f => ({ ...f, watchOnly: !f.watchOnly }))}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors ${
            filters.watchOnly
              ? 'bg-blue-600 border-blue-500 text-white'
              : 'border-slate-600 text-slate-400 hover:text-white'
          }`}
        >
          <BookmarkCheck size={12} />
          My Watch Queue
        </button>

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1.5 border border-slate-600 rounded"
          >
            <X size={12} /> Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center text-slate-400 py-12">
          No upcoming stock IPOs match your filters
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                <th className="text-left py-2 px-3">Company</th>
                <th className="text-left py-2 px-3">Ticker</th>
                <th className="text-left py-2 px-3">Exchange</th>
                <th className="text-left py-2 px-3">Expected Date</th>
                <th className="text-left py-2 px-3">Price Range</th>
                <th className="text-left py-2 px-3">Shares Offered</th>
                <th className="text-left py-2 px-3">Filing Date</th>
                <th className="text-left py-2 px-3">AI Score</th>
                <th className="text-left py-2 px-3">Watch</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => (
                <tr key={entry.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-2 px-3 font-medium text-white">{entry.company_name}</td>
                  <td className="py-2 px-3 text-blue-400 font-mono">{entry.ticker_symbol}</td>
                  <td className="py-2 px-3 text-slate-300">{entry.exchange || '—'}</td>
                  <td className="py-2 px-3 text-slate-300">{formatDate(entry.expected_date)}</td>
                  <td className="py-2 px-3 text-slate-300">{formatPrice(entry.price_range_low, entry.price_range_high)}</td>
                  <td className="py-2 px-3 text-slate-300">{formatNumber(entry.shares_offered)}</td>
                  <td className="py-2 px-3 text-slate-300">{formatDate(entry.filing_date)}</td>
                  <td className="py-2 px-3">
                    <BankerBadge evaluation={entry.banker_evaluation} isPremium={isPremium} />
                  </td>
                  <td className="py-2 px-3">
                    <WatchQueueButton
                      entry={entry}
                      watchQueue={watchQueue}
                      onAdd={onAddToWatch}
                      onRemove={onRemoveFromWatch}
                      isAddPending={isAddPending}
                      isRemovePending={isRemovePending}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Crypto Launches Tab ──────────────────────────────────────────────────────

interface CryptoFilters {
  launchType: string;
  chain: string;
  launchpad: string;
  watchOnly: boolean;
}

function CryptoLaunchTab({
  entries,
  isPremium,
  watchQueue,
  onAddToWatch,
  onRemoveFromWatch,
  isAddPending,
  isRemovePending,
}: {
  entries: IPOEntry[];
  isPremium: boolean;
  watchQueue: WatchQueueItem[];
  onAddToWatch: (ipoCalendarId: number) => void;
  onRemoveFromWatch: (itemId: number) => void;
  isAddPending: boolean;
  isRemovePending: boolean;
}) {
  const cryptoEntries = entries.filter(e => e.asset_type === 'crypto');

  const [filters, setFilters] = useState<CryptoFilters>({
    launchType: '', chain: '', launchpad: '', watchOnly: false,
  });

  const chains = useMemo(() => {
    const s = new Set(cryptoEntries.map(e => e.chain).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [cryptoEntries]);

  const launchpads = useMemo(() => {
    const s = new Set(cryptoEntries.map(e => e.launchpad).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [cryptoEntries]);

  const watchedIds = useMemo(() => new Set(watchQueue.map(w => w.ipo_calendar_id)), [watchQueue]);

  const filtered = useMemo(() => {
    return cryptoEntries.filter(e => {
      if (filters.launchType && e.sale_type !== filters.launchType) return false;
      if (filters.chain && e.chain !== filters.chain) return false;
      if (filters.launchpad && e.launchpad !== filters.launchpad) return false;
      if (filters.watchOnly && !watchedIds.has(e.id)) return false;
      return true;
    });
  }, [cryptoEntries, filters, watchedIds]);

  const clearFilters = () =>
    setFilters({ launchType: '', chain: '', launchpad: '', watchOnly: false });

  const hasFilters = filters.launchType !== '' || filters.chain !== '' || filters.launchpad !== '' || filters.watchOnly;

  return (
    <div>
      {/* Regulatory disclaimer */}
      <div className="flex items-start gap-3 bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 mb-4">
        <AlertTriangle size={16} className="text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-amber-200 text-sm">
          Crypto launch data is provided for informational purposes only. Token launches carry
          significant risk including total loss of capital. Always conduct your own research (DYOR)
          before participating in any token sale.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <select
          value={filters.launchType}
          onChange={e => setFilters(f => ({ ...f, launchType: e.target.value }))}
          className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
        >
          <option value="">All Types</option>
          <option value="ICO">ICO</option>
          <option value="IDO">IDO</option>
          <option value="IEO">IEO</option>
        </select>

        <select
          value={filters.chain}
          onChange={e => setFilters(f => ({ ...f, chain: e.target.value }))}
          className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
        >
          <option value="">All Chains</option>
          {chains.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select
          value={filters.launchpad}
          onChange={e => setFilters(f => ({ ...f, launchpad: e.target.value }))}
          className="bg-slate-700 border border-slate-600 rounded px-3 py-1.5 text-sm text-white"
        >
          <option value="">All Launchpads</option>
          {launchpads.map(l => <option key={l} value={l}>{l}</option>)}
        </select>

        <button
          onClick={() => setFilters(f => ({ ...f, watchOnly: !f.watchOnly }))}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors ${
            filters.watchOnly
              ? 'bg-blue-600 border-blue-500 text-white'
              : 'border-slate-600 text-slate-400 hover:text-white'
          }`}
        >
          <BookmarkCheck size={12} />
          My Watch Queue
        </button>

        {hasFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1.5 border border-slate-600 rounded"
          >
            <X size={12} /> Clear Filters
          </button>
        )}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center text-slate-400 py-12">
          No upcoming crypto launches match your filters
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400 text-xs uppercase">
                <th className="text-left py-2 px-3">Project</th>
                <th className="text-left py-2 px-3">Token</th>
                <th className="text-left py-2 px-3">Type</th>
                <th className="text-left py-2 px-3">Platform</th>
                <th className="text-left py-2 px-3">Chain</th>
                <th className="text-left py-2 px-3">Token Price</th>
                <th className="text-left py-2 px-3">Raise Target</th>
                <th className="text-left py-2 px-3">Sale Dates</th>
                <th className="text-left py-2 px-3">AI Score</th>
                <th className="text-left py-2 px-3">Watch</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(entry => (
                <tr key={entry.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                  <td className="py-2 px-3 font-medium text-white">{entry.company_name}</td>
                  <td className="py-2 px-3 text-purple-400 font-mono">{entry.ticker_symbol}</td>
                  <td className="py-2 px-3">
                    {entry.sale_type && (
                      <span className="px-2 py-0.5 text-xs rounded bg-slate-600 text-slate-200">
                        {entry.sale_type}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-slate-300">{entry.launchpad || '—'}</td>
                  <td className="py-2 px-3 text-slate-300">{entry.chain || '—'}</td>
                  <td className="py-2 px-3 text-slate-300">
                    {entry.token_price != null ? `$${entry.token_price}` : '—'}
                  </td>
                  <td className="py-2 px-3 text-slate-300">{entry.raise_target || '—'}</td>
                  <td className="py-2 px-3 text-slate-300 text-xs">
                    {entry.sale_start_date
                      ? `${formatDate(entry.sale_start_date)} – ${formatDate(entry.sale_end_date)}`
                      : formatDate(entry.expected_date)}
                  </td>
                  <td className="py-2 px-3">
                    <BankerBadge evaluation={entry.banker_evaluation} isPremium={isPremium} />
                  </td>
                  <td className="py-2 px-3">
                    <WatchQueueButton
                      entry={entry}
                      watchQueue={watchQueue}
                      onAdd={onAddToWatch}
                      onRemove={onRemoveFromWatch}
                      isAddPending={isAddPending}
                      isRemovePending={isRemovePending}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function IPOCalendar() {
  const [activeTab, setActiveTab] = useState<'stock' | 'crypto'>('stock');
  const [degradedDismissed, setDegradedDismissed] = useState(false);
  const queryClient = useQueryClient();

  const { hasPermission } = usePermissionContext();
  const isPremium = hasPermission('feature:banker-avatar-3d');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['ipo', 'calendar'],
    queryFn: () => ipoService.getCalendar(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: watchQueue = [] } = useQuery({
    queryKey: ['ipo', 'watch-queue'],
    queryFn: () => ipoService.getWatchQueue(),
    staleTime: 60 * 1000,
  });

  const addMutation = useMutation({
    mutationFn: (ipoCalendarId: number) => ipoService.addToWatchQueue(ipoCalendarId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipo', 'watch-queue'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (itemId: number) => ipoService.removeFromWatchQueue(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipo', 'watch-queue'] });
    },
  });

  const stockCount = data?.entries.filter(e => e.asset_type === 'stock').length ?? 0;
  const cryptoCount = data?.entries.filter(e => e.asset_type === 'crypto').length ?? 0;

  const hasDegradedProvider =
    data !== undefined &&
    Object.values(data.providers_status).some(s => s !== 'ok' && s !== 'healthy');

  const lastUpdated = data?.last_updated
    ? new Date(data.last_updated).toLocaleString()
    : null;

  return (
    <PermissionGate
      permission="feature:ipo-calendar"
      fallback={
        <div className="p-8">
          <UpgradeNudge tier="pro" />
        </div>
      }
    >
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Rocket className="text-blue-400" size={28} />
          <div>
            <h1 className="text-2xl font-bold text-white">IPO Calendar</h1>
            <p className="text-slate-400 text-sm">Upcoming stock IPOs and crypto token launches</p>
          </div>
          {watchQueue.length > 0 && (
            <div className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 rounded-lg text-sm text-slate-300">
              <BookmarkCheck size={14} className="text-blue-400" />
              {watchQueue.length} watching
            </div>
          )}
        </div>

        {/* Degraded provider warning */}
        {hasDegradedProvider && !degradedDismissed && (
          <div className="flex items-start justify-between gap-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-yellow-400 flex-shrink-0" />
              <p className="text-yellow-200 text-sm">
                Some data providers are currently unavailable. Data may be incomplete.
              </p>
            </div>
            <button
              onClick={() => setDegradedDismissed(true)}
              className="text-yellow-400 hover:text-yellow-200 flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-1 border-b border-slate-700">
          <button
            onClick={() => setActiveTab('stock')}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              activeTab === 'stock'
                ? 'bg-slate-700 text-white border-b-2 border-blue-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Stock IPOs {data && `(${stockCount})`}
          </button>
          <button
            onClick={() => setActiveTab('crypto')}
            className={`px-4 py-2 text-sm font-medium rounded-t transition-colors ${
              activeTab === 'crypto'
                ? 'bg-slate-700 text-white border-b-2 border-purple-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            Crypto Launches {data && `(${cryptoCount})`}
          </button>
        </div>

        {/* Last updated */}
        {lastUpdated && (
          <p className="text-xs text-slate-500 mb-4">Last updated: {lastUpdated}</p>
        )}

        {/* Content */}
        <div className="bg-slate-800 rounded-lg p-4">
          {isLoading && (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-10 bg-slate-700 rounded" />
              ))}
            </div>
          )}

          {isError && (
            <div className="text-center py-12">
              <p className="text-slate-400 mb-4">Failed to load IPO calendar data.</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
              >
                Retry
              </button>
            </div>
          )}

          {data && activeTab === 'stock' && (
            <StockIPOTab
              entries={data.entries}
              isPremium={isPremium}
              watchQueue={watchQueue}
              onAddToWatch={addMutation.mutate}
              onRemoveFromWatch={removeMutation.mutate}
              isAddPending={addMutation.isPending}
              isRemovePending={removeMutation.isPending}
            />
          )}
          {data && activeTab === 'crypto' && (
            <CryptoLaunchTab
              entries={data.entries}
              isPremium={isPremium}
              watchQueue={watchQueue}
              onAddToWatch={addMutation.mutate}
              onRemoveFromWatch={removeMutation.mutate}
              isAddPending={addMutation.isPending}
              isRemovePending={removeMutation.isPending}
            />
          )}
        </div>
      </div>
    </PermissionGate>
  );
}
